:host { all: initial; display: block; }
* { box-sizing: border-box; }

/* The widget sizes to the slot it's dropped into, not the viewport, so it adapts
   inside a hero, a sidebar, or a narrow column. Hence container queries below.
   Tokens mirror visamundi-website/DESIGN.md: steel blue for structure, indigo
   kept rare, 1px rules instead of shadows, the mascot as the only warmth. */
.vm {
  container-type: inline-size;

  --vm-brand-50: #f0f7fc;
  --vm-brand-100: #dfeef8;
  --vm-brand-200: #b8d8ee;
  --vm-brand-500: #3279a8;
  --vm-brand-600: #216592;
  --vm-brand-700: #1a5078;
  --vm-brand-900: #102d44;

  --vm-surface: #ffffff;
  --vm-head: var(--vm-brand-50);
  --vm-panel: #fbfbfb;
  --vm-field: #ffffff;
  --vm-field-hover: #fbfbfb;
  --vm-ink: #1f1f1f;
  --vm-ink-2: #3f4650;
  --vm-muted: #657383;
  --vm-rule: #e4e4e7;
  --vm-rule-strong: #d4d4d8;
  --vm-halo: #b8d8ee;
  --vm-sk: #eceef1;
  --vm-focus: rgb(50 121 168 / 0.28);

  --vm-display: 'Geist', 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
  --vm-body: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  --vm-ease: cubic-bezier(0.25, 1, 0.5, 1); /* ease-out-quart */

  --vm-pad: clamp(1rem, 5cqi, 1.75rem);

  position: relative;
  font-family: var(--vm-body);
  font-size: 15px;
  line-height: 1.5;
  color: var(--vm-ink);
  background: var(--vm-surface);
  border: 1px solid var(--vm-rule);
  border-radius: 28px;
  max-width: 36rem;
  -webkit-font-smoothing: antialiased;
  /* Resting shadow: allowed because the card holds a form (DESIGN.md §4). */
  box-shadow: 0 1px 2px 0 rgb(16 45 68 / 0.05);
}
.vm[data-theme='dark'] {
  --vm-surface: #102d44;
  --vm-head: #15384f;
  --vm-panel: #15384f;
  --vm-field: #0c2436;
  --vm-field-hover: #0a2031;
  --vm-ink: #f3f6f9;
  --vm-ink-2: #d5e0ea;
  --vm-muted: #9fb4c6;
  --vm-rule: #22475f;
  --vm-rule-strong: #2e5873;
  --vm-halo: #3279a8;
  --vm-sk: #1f4660;
  --vm-focus: rgb(184 216 238 / 0.35);
  --vm-brand-500: #6fa8cf;
  --vm-brand-600: #8bbbdc;
  --vm-brand-700: #b8d8ee;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.25);
}

/* Header band ---------------------------------------------------------------- */
.vm__head {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  min-height: clamp(5.5rem, 24cqi, 7.25rem);
  padding: var(--vm-pad) var(--vm-pad) clamp(0.9rem, 3.5cqi, 1.25rem);
  background: var(--vm-head);
  border-bottom: 1px solid var(--vm-rule);
  /* Round the tint itself: no overflow:hidden on .vm, it would clip the listbox. */
  border-radius: 27px 27px 0 0;
}
.vm__title {
  position: relative;
  z-index: 1;
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 22ch;
  font-family: var(--vm-display);
  font-size: clamp(1.15rem, 4.6cqi, 1.45rem);
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: -0.015em;
  color: var(--vm-brand-900);
  text-wrap: balance;
}
.vm[data-theme='dark'] .vm__title { color: var(--vm-ink); }
.vm__title strong { font-weight: 700; color: var(--vm-brand-600); white-space: nowrap; }
.vm__nw { white-space: nowrap; }
.vm__logo-wrap { display: inline-flex; align-items: center; vertical-align: -0.3em; }
.vm__logo { height: 1.3em; width: auto; display: block; }

/* Looping holds a passport: on-topic for the widget's job. Decorative only. */
.vm__mascot {
  position: relative;
  flex: none;
  width: clamp(76px, 25cqi, 120px);
  align-self: flex-end;
  margin-left: auto;
  margin-top: -0.5rem;
  margin-right: -0.35rem;
  margin-bottom: calc(-1 * clamp(0.9rem, 3.5cqi, 1.25rem) - 1px);
  pointer-events: none;
  user-select: none;
}
/* Pastel halo behind the mascot (radial fill, no blur filter to repaint). */
.vm__mascot::before {
  content: '';
  position: absolute;
  inset: 4% -8% -6% -8%;
  border-radius: 50%;
  background: radial-gradient(closest-side, var(--vm-halo), transparent);
  opacity: 0.85;
}
.vm__mascot img {
  position: relative;
  display: block;
  width: 100%;
  height: auto;
}

/* Body ----------------------------------------------------------------------- */
.vm__body { padding: clamp(1rem, 4.5cqi, 1.5rem) var(--vm-pad) var(--vm-pad); }

/* Route: origin, flight path, destination */
.vm__form {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: end;
  gap: 0.5rem 0.6rem;
}
.vm__field { display: flex; flex-direction: column; gap: 0.4rem; min-width: 0; }
.vm__field-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--vm-ink-2);
}
.vm__path {
  width: 2.25rem;
  height: 2.75rem; /* matches the field height so the plane sits on its centre */
  color: var(--vm-brand-500);
  display: grid;
  place-items: center;
  background:
    repeating-linear-gradient(to right, var(--vm-brand-200) 0 3px, transparent 3px 7px)
    left center / 100% 1.5px no-repeat;
}
.vm[data-theme='dark'] .vm__path {
  background:
    repeating-linear-gradient(to right, var(--vm-rule-strong) 0 3px, transparent 3px 7px)
    left center / 100% 1.5px no-repeat;
}
.vm__path-plane {
  display: grid;
  place-items: center;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  background: var(--vm-surface);
  box-shadow: inset 0 0 0 1px var(--vm-rule-strong);
}
.vm__path-plane svg { width: 0.9rem; height: 0.9rem; transform: rotate(90deg); }

/* Combobox (replaces native <select> so options can show flags) */
.vm__combo { position: relative; min-width: 0; }
.vm__combo-btn {
  width: 100%;
  min-height: 2.75rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.75rem;
  font: inherit;
  color: var(--vm-ink);
  text-align: left;
  background: var(--vm-field);
  border: 1px solid var(--vm-rule-strong);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s var(--vm-ease), box-shadow 0.2s var(--vm-ease),
    background-color 0.2s var(--vm-ease);
}
.vm__combo-btn:hover { background: var(--vm-field-hover); border-color: var(--vm-muted); }
.vm__combo-btn:focus-visible,
.vm__combo.is-open .vm__combo-btn {
  outline: none;
  border-color: var(--vm-brand-500);
  box-shadow: 0 0 0 3px var(--vm-focus);
}
.vm__combo-value { flex: 1; display: flex; align-items: center; gap: 0.6rem; min-width: 0; }
.vm__combo-text,
.vm__combo-placeholder { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vm__combo-placeholder { color: var(--vm-muted); }
.vm__combo-caret {
  flex: none;
  width: 0.5rem;
  height: 0.5rem;
  margin: -0.2rem 0.1rem 0 0;
  border-right: 1.5px solid var(--vm-muted);
  border-bottom: 1.5px solid var(--vm-muted);
  transform: rotate(45deg);
  transition: transform 0.25s var(--vm-ease);
}
.vm__combo.is-open .vm__combo-caret { transform: translateY(0.2rem) rotate(225deg); }

/* Round flags (flagcdn.com) */
.vm__flag {
  flex: none;
  width: 1.3em;
  height: 1.3em;
  border-radius: 50%;
  object-fit: cover;
  background: var(--vm-sk);
  box-shadow: inset 0 0 0 1px rgb(16 45 68 / 0.12);
}
.vm__flag--empty { display: inline-block; }

/* Listbox: the one floating surface, so it earns a shadow. */
.vm__listbox {
  position: absolute;
  z-index: 30;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 0.3rem;
  list-style: none;
  max-height: 16rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--vm-field);
  border: 1px solid var(--vm-rule);
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgb(16 45 68 / 0.1), 0 14px 32px -12px rgb(16 45 68 / 0.28);
  scrollbar-width: thin;
}
.vm__option {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.6rem;
  border-radius: 8px;
  cursor: pointer;
}
.vm__option-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vm__option.is-active { background: var(--vm-brand-50); }
.vm[data-theme='dark'] .vm__option.is-active { background: var(--vm-head); }
.vm__option[aria-selected='true'] { font-weight: 600; color: var(--vm-brand-700); }

/* Result --------------------------------------------------------------------- */
.vm__result:not(:empty) { margin-top: clamp(1rem, 4cqi, 1.35rem); }
/* Flat panel: tint + 1px rule, no shadow (it is not clickable). */
.vm__card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.65rem;
  padding: clamp(1rem, 4cqi, 1.35rem);
  background: var(--vm-panel);
  border: 1px solid var(--vm-rule);
  border-radius: 20px;
  animation: vm-in 0.32s var(--vm-ease) both;
}
@keyframes vm-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
}

/* Answer reveal: the panel settles in, then each line rises in reading order
   (badge, destination, statement, CTAs), 70ms apart. Transform/opacity only. */
.vm__card--reveal { animation: vm-panel 0.42s var(--vm-ease) both; }
.vm__card--reveal > * {
  animation: vm-rise 0.5s var(--vm-ease) both;
  animation-delay: calc(80ms + var(--vm-i, 0) * 70ms);
}
.vm__card--reveal .vm__badge-dot {
  animation: vm-dot 0.4s var(--vm-ease) both;
  animation-delay: 220ms;
}
.vm__card--reveal .vm__flag--lg {
  animation: vm-flag 0.5s var(--vm-ease) both;
  animation-delay: 170ms;
}
@keyframes vm-panel {
  from { opacity: 0; transform: translateY(6px) scale(0.985); }
  to { opacity: 1; transform: none; }
}
@keyframes vm-rise {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
}
@keyframes vm-dot {
  from { transform: scale(0); }
  to { transform: scale(1); }
}
@keyframes vm-flag {
  from { opacity: 0; transform: scale(0.6) rotate(-20deg); }
  to { opacity: 1; transform: none; }
}

/* Looping acknowledges the answer: a small lift and tilt, then settles. */
.vm__mascot img { transform-origin: 50% 90%; }
.vm__mascot.is-cheering img { animation: vm-cheer 0.7s var(--vm-ease) both; }
@keyframes vm-cheer {
  0% { transform: none; }
  35% { transform: translateY(-6px) rotate(-5deg); }
  100% { transform: none; }
}

/* Status badge: sentence case pill with a dot. Status colours are only used
   for verdicts, and red is avoided (DESIGN.md: no anxiety-inducing alerts). */
.vm__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.45em;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1;
  padding: 0.42em 0.75em 0.42em 0.6em;
  border-radius: 999px;
  color: var(--vm-b-fg);
  background: var(--vm-b-bg);
}
.vm__badge-dot { width: 0.45em; height: 0.45em; border-radius: 50%; background: currentColor; }
.vm__badge--good { --vm-b-fg: #15803d; --vm-b-bg: #e6f6ec; }
.vm__badge--warn { --vm-b-fg: #a15c07; --vm-b-bg: #fdf1dc; }
.vm__badge--teal,
.vm__badge--info { --vm-b-fg: #1a5078; --vm-b-bg: #dfeef8; }
.vm__badge--bad { --vm-b-fg: #f3f6f9; --vm-b-bg: #1f1f1f; }
.vm__badge--muted { --vm-b-fg: #3f4650; --vm-b-bg: #eceef1; }
.vm[data-theme='dark'] .vm__badge--good { --vm-b-fg: #86efac; --vm-b-bg: rgb(134 239 172 / 0.12); }
.vm[data-theme='dark'] .vm__badge--warn { --vm-b-fg: #fcd34d; --vm-b-bg: rgb(252 211 77 / 0.12); }
.vm[data-theme='dark'] .vm__badge--teal,
.vm[data-theme='dark'] .vm__badge--info { --vm-b-fg: #b8d8ee; --vm-b-bg: rgb(184 216 238 / 0.14); }
.vm[data-theme='dark'] .vm__badge--bad { --vm-b-fg: #102d44; --vm-b-bg: #f3f6f9; }
.vm[data-theme='dark'] .vm__badge--muted { --vm-b-fg: #d5e0ea; --vm-b-bg: rgb(213 224 234 / 0.12); }

.vm__dest { display: flex; align-items: center; gap: 0.55rem; margin-top: 0.15rem; }
.vm__flag--lg { width: 1.6em; height: 1.6em; }
.vm__dest strong {
  font-family: var(--vm-display);
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--vm-ink);
}

.vm__statement {
  margin: 0;
  max-width: 62ch;
  color: var(--vm-ink-2);
  line-height: 1.6;
  text-wrap: pretty;
}
.vm__error { color: #b91c1c; }
.vm[data-theme='dark'] .vm__error { color: #fca5a5; }

/* CTAs: pill buttons, 44px, as on visamundi.co */
.vm__ctas { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.35rem; }
.vm__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  min-height: 2.75rem;
  padding: 0 1.25rem;
  background: var(--vm-brand-500);
  color: #ffffff;
  border-radius: 999px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.93rem;
  transition: background-color 0.2s var(--vm-ease), border-color 0.2s var(--vm-ease),
    transform 0.2s var(--vm-ease), box-shadow 0.2s var(--vm-ease);
}
.vm__cta::after {
  content: '';
  width: 0.4em;
  height: 0.4em;
  border-top: 1.5px solid currentColor;
  border-right: 1.5px solid currentColor;
  transform: rotate(45deg);
  transition: transform 0.2s var(--vm-ease);
}
.vm__cta:hover {
  background: var(--vm-brand-600);
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgb(16 45 68 / 0.1);
}
.vm__cta:hover::after { transform: translateX(2px) rotate(45deg); }
.vm__cta:focus-visible { outline: 2px solid var(--vm-brand-500); outline-offset: 2px; }
.vm[data-theme='dark'] .vm__cta { background: #f3f6f9; color: #102d44; }
.vm[data-theme='dark'] .vm__cta:hover { background: #ffffff; }
/* Secondary CTA (Visamundi destination page): outlined, lower emphasis. */
.vm__cta--alt,
.vm[data-theme='dark'] .vm__cta--alt {
  background: var(--vm-field);
  color: var(--vm-ink);
  box-shadow: inset 0 0 0 1px var(--vm-rule-strong);
}
.vm__cta--alt:hover,
.vm[data-theme='dark'] .vm__cta--alt:hover {
  background: var(--vm-field);
  box-shadow: inset 0 0 0 1px var(--vm-muted), 0 4px 6px -1px rgb(16 45 68 / 0.1);
}

/* Loading skeleton */
.vm__card--loading { gap: 0.7rem; }
.vm__sk {
  display: block;
  width: 100%;
  height: 0.8rem;
  border-radius: 999px;
  background: var(--vm-sk);
  animation: vm-pulse 1.4s ease-in-out infinite;
}
.vm__sk--badge { width: 7rem; height: 1.45rem; }
.vm__sk--title { width: 45%; height: 1.2rem; }
.vm__sk--short { width: 60%; }
@keyframes vm-pulse { 50% { opacity: 0.55; } }
.vm__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* Compact: for fixed-height frames (partner iframes are 520x420, scrolling="no").
   Shorter header, tighter card and listbox so a full answer fits without scroll. */
.vm[data-size='compact'] { --vm-pad: 1.15rem; }
.vm[data-size='compact'] .vm__head {
  min-height: 0;
  padding: 0.8rem var(--vm-pad) 0.65rem;
}
.vm[data-size='compact'] .vm__title { max-width: none; font-size: 1.15rem; }
.vm[data-size='compact'] .vm__mascot {
  width: 64px;
  margin-top: 0;
  margin-bottom: calc(-0.65rem - 1px);
}
.vm[data-size='compact'] .vm__body { padding: 0.8rem var(--vm-pad) var(--vm-pad); }
.vm[data-size='compact'] .vm__result:not(:empty) { margin-top: 0.8rem; }
.vm[data-size='compact'] .vm__card { gap: 0.45rem; padding: 0.85rem 1rem; }
.vm[data-size='compact'] .vm__ctas { margin-top: 0.15rem; }
.vm[data-size='compact'] .vm__cta { min-height: 2.4rem; }
.vm[data-size='compact'] .vm__listbox { max-height: 11rem; }

/* Narrow container: stack the route, turn the flight path vertical */
@container (max-width: 26rem) {
  .vm__form { grid-template-columns: 1fr; align-items: stretch; }
  .vm__path {
    width: 100%;
    height: 1.9rem;
    background:
      repeating-linear-gradient(to bottom, var(--vm-brand-200) 0 3px, transparent 3px 7px)
      center / 1.5px 100% no-repeat;
  }
  .vm__path-plane svg { transform: rotate(180deg); }
  .vm__title { font-size: 1.1rem; }
}

@media (prefers-reduced-motion: reduce) {
  .vm__card, .vm__sk, .vm__card--reveal, .vm__card--reveal > *,
  .vm__card--reveal .vm__badge-dot, .vm__card--reveal .vm__flag--lg,
  .vm__mascot.is-cheering img { animation: none; }
  .vm__combo-btn, .vm__combo-caret, .vm__cta, .vm__cta::after { transition: none; }
  .vm__cta:hover { transform: none; }
}
